// Tab "Ticket cấp dưới": góp ý của shop / CTV ngay dưới đơn vị đang chọn. Chủ / quản lý tiếp nhận, giải quyết.

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { pageRange, TICKET_PAGE_SIZE, TICKET_STATUS_LABEL, type TicketStatus } from '@shoptrack/common/tickets'
import { get, post } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { IndexBadge } from '@/components/ui/data-table'
import { formatDateTime } from '@/lib/format'
import { TicketHandling, type TicketHandlingFields } from './TicketHandling'

interface DownlineTicket extends TicketHandlingFields {
  id: string; shopName: string | null; senderName: string | null; senderPhone: string | null
  category: string; title: string; content: string; status: TicketStatus; createdAt: string
  /** Server tính theo người đang xem: chưa ai nhận, hoặc chính mình đã nhận. */
  canResolve: boolean
}

const FILTERS = [
  { value: 'pending', label: 'Chờ tiếp nhận' },
  { value: 'in_progress', label: 'Đang xử lý' },
  { value: 'resolved', label: 'Đã giải quyết' },
  { value: 'all', label: 'Tất cả' },
] as const

export function DownlineTickets({ focusId }: { focusId: string | null }) {
  const qc = useQueryClient()
  const [status, setStatus] = useState<(typeof FILTERS)[number]['value']>(focusId ? 'all' : 'pending')
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [page, setPage] = useState(0)
  useEffect(() => setPage(0), [status])

  const q = useQuery({
    queryKey: ['downline-tickets', status, page],
    queryFn: () => {
      const sp = new URLSearchParams()
      if (status !== 'all') sp.set('status', status)
      if (page) sp.set('page', String(page))
      return get<{ tickets: DownlineTicket[]; total: number }>(`/feedback/downline?${sp.toString()}`)
    },
  })

  useEffect(() => {
    if (!focusId || !q.data) return
    document.getElementById(`ticket-${focusId}`)?.scrollIntoView({ block: 'center' })
  }, [focusId, q.data])

  const refresh = () => qc.invalidateQueries({ queryKey: ['downline-tickets'] })
  const onError = (e: any) => {
    toast.error(e?.message ?? 'Không thực hiện được')
    refresh()
  }
  const take = useMutation({
    mutationFn: (id: string) => post(`/feedback/${id}/take`),
    onSuccess: () => { toast.success('Đã tiếp nhận'); refresh() },
    onError,
  })
  const resolve = useMutation({
    mutationFn: (v: { id: string; note: string }) => post(`/feedback/${v.id}/resolve`, { note: v.note }),
    onSuccess: (_r, v) => {
      toast.success('Đã giải quyết')
      setNotes((n) => ({ ...n, [v.id]: '' }))
      refresh()
    },
    onError,
  })

  const items = q.data?.tickets ?? []
  const total = q.data?.total ?? 0
  const range = pageRange(page, TICKET_PAGE_SIZE, total)
  return (
    <div className="space-y-4 rounded-xl bg-raise p-5 shadow-card sm:p-6">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button key={f.value} size="sm" variant={status === f.value ? 'default' : 'outline'} onClick={() => setStatus(f.value)}>{f.label}</Button>
        ))}
      </div>
      {q.isLoading ? (
        <p className="py-10 text-center text-sm text-muted">Đang tải…</p>
      ) : q.isError ? (
        <p className="py-10 text-center text-sm text-danger">{(q.error as any)?.message ?? 'Không tải được góp ý'}</p>
      ) : !items.length ? (
        <p className="py-10 text-center text-sm text-muted">Không có góp ý</p>
      ) : (
        <div className="divide-y divide-rule">
          {items.map((t, i) => {
            const note = notes[t.id] ?? ''
            return (
              <div
                key={t.id}
                id={`ticket-${t.id}`}
                className={`space-y-2 py-4 first:pt-0 last:pb-0 ${t.id === focusId ? 'rounded-lg px-2 ring-2 ring-accent/40' : ''}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
                  <span className="flex min-w-0 items-center gap-2">
                    <IndexBadge n={range.offset + i + 1} />
                    <span className="font-medium text-ink">{t.shopName ?? t.senderName ?? '—'}{t.senderPhone ? ` · ${t.senderPhone}` : ''}</span>
                  </span>
                  <span>{formatDateTime(t.createdAt)} · <b>{TICKET_STATUS_LABEL[t.status]}</b></span>
                </div>
                <h3 className="text-sm font-semibold text-ink">{t.title}</h3>
                <p className="whitespace-pre-wrap rounded-lg border border-rule/60 bg-paper/60 p-3 text-xs text-inkSoft">{t.content}</p>
                <TicketHandling t={t} />
                {t.status === 'pending' && (
                  <Button size="sm" variant="outline" disabled={take.isPending} onClick={() => take.mutate(t.id)}>Tiếp nhận</Button>
                )}
                {t.canResolve && (
                  <div className="space-y-2">
                    <Textarea
                      rows={2}
                      maxLength={2000}
                      value={note}
                      onChange={(e) => setNotes((n) => ({ ...n, [t.id]: e.target.value }))}
                      placeholder="Ghi chú kết quả (bắt buộc)"
                    />
                    <Button size="sm" disabled={!note.trim() || resolve.isPending} onClick={() => resolve.mutate({ id: t.id, note })}>Giải quyết</Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      {total > 0 && (
        <div className="flex items-center justify-end gap-2 text-xs text-muted">
          <span>{range.from}–{range.to} / {total}</span>
          <Button size="sm" variant="outline" disabled={!range.hasPrev} onClick={() => setPage((p) => p - 1)}>Trước</Button>
          <Button size="sm" variant="outline" disabled={!range.hasNext} onClick={() => setPage((p) => p + 1)}>Sau</Button>
        </div>
      )}
    </div>
  )
}
